<template>
  <section :class="[$style.container,getContainerClass]">
    <div
      :class="[$style.operatorLabel, getComponentOnRowClass]"
    >
      {{ operatorLabel }}
    </div>
    <div>
      <Select
        v-model="selectedAttribute"
        :autofocus="false"
        :class="[$style.minWidthOverride, getComponentOnRowClass]"
        dataTestId="attributeSelect"
        :focus="false"
        :isDisabled="false"
        :isHelperTextMarkdown="false"
        :isReadonly="false"
        name="attribute"
        :options="dropdownOptions"
        placeholder="select one"
        rules="required"
        :showValidationMessage="true"
        @input="handleFilterChanges"
      />
    </div>
    <div :class="getOperatorWrapperClass">
      <Select
        v-if="!isChildItem"
        v-model="selectedOperator"
        :autofocus="false"
        :class="$style.minWidthOverride"
        dataTestId="operatorSelect"
        :focus="false"
        :isHelperTextMarkdown="false"
        :isReadonly="false"
        :options="options"
        :required="false"
        :showValidationMessage="false"
        @input="handleFilterChanges"
      />
      <span v-else>Or</span>
    </div>
    <div>
      <InputText
        v-model="inputValue"
        :autofocus="false"
        :class="$style.minWidthOverride"
        :focus="false"
        :isDisabled="false"
        :isHelperTextMarkdown="false"
        :isReadonly="false"
        placeholder="attribute value"
        :required="false"
        :showValidationMessage="false"
        @input="handleFilterChanges"
      />
      <div
        v-if="showIncludesLink"
        :key="filter.id"
        :class="$style.includesLinkWrapper"
      >
        <ButtonLink
          text="Add value"
          @click="addIncludesValue()"
        />
      </div>
    </div>
    <div>
      <ButtonDelete
        data-test-id="deleteFilterButton"
        @click="deleteFilterRow"
      />
    </div>
  </section>
</template>

<script>
import {
  ButtonDelete, ButtonLink, InputText, Select,
} from '@jumpcloud/ui-components';
import { cloneDeep } from 'lodash';
import Util from 'client/util/Util';

const { convertPascalCaseToWords } = Util;

export default {
  name: 'MembershipSuggestionFilterRow',
  components: {
    ButtonDelete,
    ButtonLink,
    InputText,
    Select,
  },
  props: {
    /**
     * List of available attributes.
     */
    attributes: {
      type: Array,
      required: true,
    },
    /**
     * Filter provides selected attribute name and value.
     */
    filter: {
      type: Object,
      required: false,
      default: () => {},
    },
    /**
     * Filter provides selected attribute name and value.
     */
    filterIndex: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      options: [{ name: 'equals', value: 'eq' }, { name: 'not equals', value: 'ne' }],
      selectedAttribute: this.filter.field,
      selectedOperator: this.filter.operator !== 'in' ? this.filter.operator : 'eq',
      inputValue: this.filter.value,
      localAttributes: [],
    };
  },
  computed: {
    dropdownOptions() {
      return this.localAttributes.length
        ? this.localAttributes.map(attr => ({ name: convertPascalCaseToWords(attr, 'remove'), value: attr }))
        : [];
    },

    getComponentOnRowClass() {
      return this.isChildItem ? this.$style.hidden : '';
    },

    getContainerClass() {
      return this.isChildItem ? this.$style.childContainer : '';
    },

    getOperatorWrapperClass() {
      return this.isChildItem ? this.$style.selectOperatorWrapper : '';
    },

    hasChildren() {
      return this.filter.type === 'parent' && !this.filter.isLastItem;
    },

    isChildItem() {
      return (this.filter.type && this.filter.type === 'child');
    },

    operatorLabel() {
      return this.filterIndex > 0 ? 'And' : 'When';
    },

    showIncludesLink() {
      return (this.filter.operator === 'eq'
              || (
                this.filter.operator === 'in'
                && this.filter.isLastItem
                && this.filter.type !== 'parent'
              )
      );
    },
  },

  watch: {
    attributes() {
      this.updateLocalAttributes();
    },
  },
  created() {
    this.updateLocalAttributes();
  },

  methods: {
    addIncludesValue() {
      const filter = {
        field: this.selectedAttribute,
        id: this.filter.id,
        isLastItem: this.filter.isLastItem,
        operator: 'in',
        parentIndex: this.filter.parentIndex,
        parentIndexUI: this.filter.parentIndexUI,
        type: this.filter.type || 'child',
        value: this.inputValue,
      };

      this.$emit('addIncludesValue', this.filterIndex, filter);
    },

    deleteFilterRow() {
      this.$emit('deleteFilter', this.filterIndex, this.filter);
    },

    handleFilterChanges() {
      const isConvertedOperator = this.filter.operator === 'in' && this.selectedOperator === 'eq';
      const filter = {
        field: this.selectedAttribute,
        id: this.filter.id,
        isLastItem: this.filter.isLastItem,
        operator: isConvertedOperator ? this.filter.operator : this.selectedOperator,
        parentIndex: this.filter.parentIndex,
        parentIndexUI: this.filter.parentIndexUI,
        type: this.filter.type || 'child',
        value: this.inputValue.trim(),
      };

      this.$emit('updateFilter', this.filterIndex, filter);

      // children have to be removed if changing operators away from 'eq'
      if (this.selectedOperator !== 'eq' && this.hasChildren) {
        this.$emit('clearChildFilters', this.filterIndex, this.filter.parentIndexUI);
      }
    },
    updateLocalAttributes() {
      const hasFilter = Object.keys(this.filter).length && this.filter.field;
      const includesAttribute = this.attributes.includes(this.filter.field);

      // set or refresh
      this.localAttributes = cloneDeep(this.attributes);

      if (this.localAttributes && hasFilter && !includesAttribute) {
        this.localAttributes.push(this.filter.field);
      }
    },
  },
};
</script>

<style module>

  .childContainer {
    margin-top: -0.94rem;
  }

  .container {
    display: grid;
    gap: var(--columnGap);
    grid-template-columns: var(--columnSizes);
  }

  /* Override for min-width on form ui-components */
  .container .minWidthOverride > div {
    min-width: unset;
  }

  .hidden {
    visibility: hidden;
  }

  .includesLinkWrapper {
    display: flex;
    justify-content: flex-end;
    padding: 0.6rem 0 1rem;
  }

  .operatorLabel {
    font-size: 14px;
    line-height: 16px;
    margin-top: 8px;
  }

  .selectOperatorWrapper {
    display: flex;
    justify-content: flex-end;
    padding: 0.567rem 0;
  }
</style>
